import React, { useState } from 'react';
import { Search, Plus, Eye, Edit2, XCircle } from 'lucide-react';
import AddSubscriptionModal    from './Addsubscriptionmodal';
import EditSubscriptionModal   from './Editsubscriptionmodal';
import ViewSubscriptionModal   from './Viewsubscriptionmodal';
import CancelSubscriptionModal from './Cancelsubscriptionmodal';

const initialSubscribers = [
  { id: '#SUB001', name: 'Jake Morrison', email: 'jake@vegas.com', plan: 'Pro Racer', billing: 'Monthly', start: 'Jan 12, 2025', expiry: 'Feb 12, 2025', amount: '$29.00', status: 'Active' },
  { id: '#SUB002', name: 'Sarah Connell', email: 'sarah@vegas.com', plan: 'Starter', billing: 'Annual', start: 'Jan 15, 2025', expiry: 'Jan 15, 2026', amount: '$86.00', status: 'Active' },
  { id: '#SUB003', name: 'Mike Torres', email: 'mike@vegas.com', plan: 'Champion', billing: 'Monthly', start: 'Feb 01, 2025', expiry: 'Mar 01, 2025', amount: '$59.00', status: 'Expired' },
  { id: '#SUB004', name: 'Emma Walsh', email: 'emma@vegas.com', plan: 'Starter', billing: 'Monthly', start: 'Feb 10, 2025', expiry: 'Mar 10, 2025', amount: '$9.00', status: 'Active' },
  { id: '#SUB005', name: 'Ryan Park', email: 'ryan@vegas.com', plan: 'Pro Racer', billing: 'Annual', start: 'Mar 03, 2025', expiry: 'Mar 03, 2026', amount: '$278.00', status: 'Active' },
  { id: '#SUB006', name: 'Nina Patel', email: 'nina@vegas.com', plan: 'Champion', billing: 'Annual', start: 'Mar 14, 2025', expiry: 'Mar 14, 2026', amount: '$566.00', status: 'Active' },
  { id: '#SUB007', name: 'Chris Blake', email: 'chris@vegas.com', plan: 'Starter', billing: 'Monthly', start: 'Apr 02, 2025', expiry: 'May 02, 2025', amount: '$9.00', status: 'Cancelled' },
  { id: '#SUB008', name: 'Olivia Grant', email: 'olivia@vegas.com', plan: 'Pro Racer', billing: 'Monthly', start: 'Apr 20, 2025', expiry: 'May 20, 2025', amount: '$29.00', status: 'Active' },
];

const statusBadge = status => {
  if (status === 'Active')    return 'badge-success';
  if (status === 'Expired')   return 'badge-danger';
  if (status === 'Cancelled') return 'badge-warning';
  return 'badge-info';
};

function Subscription() {
  const [subscribers, setSubscribers] = useState(initialSubscribers);
  const [search, setSearch]     = useState('');
  const [filter, setFilter]     = useState('All');

  // Modal states
  const [showAdd, setShowAdd]               = useState(false);
  const [editSub, setEditSub]               = useState(null);
  const [viewSub, setViewSub]               = useState(null);
  const [cancelSub, setCancelSub]           = useState(null);

  const filtered = subscribers.filter(s => {
    const matchSearch =
      s.name.toLowerCase().includes(search.toLowerCase()) ||
      s.email.toLowerCase().includes(search.toLowerCase()) ||
      s.plan.toLowerCase().includes(search.toLowerCase());
    const matchFilter = filter === 'All' || s.status === filter;
    return matchSearch && matchFilter;
  });

  const handleAdd    = (newSub)     => setSubscribers(prev => [newSub, ...prev]);
  const handleSave   = (updated)    => setSubscribers(prev => prev.map(s => s.id === updated.id ? updated : s));
  const handleCancel = (id)         => setSubscribers(prev => prev.map(s => s.id === id ? { ...s, status: 'Cancelled' } : s));

  return (
    <div className="page-container">

      {/* Header */}
      <div className="page-header">
        <div>
          <h1 className="page-title">Subscriptions</h1>
          <p className="page-subtitle">Manage all Vegas Karts subscription plans and members.</p>
        </div>
        <button className="btn-primary" onClick={() => setShowAdd(true)}>
          <Plus size={15} /> Add Subscription
        </button>
      </div>

      {/* Stats */}
      <div className="stats-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', marginBottom: 24 }}>
        {[
          { label: 'Total Subscribers', value: subscribers.length },
          { label: 'Active',            value: subscribers.filter(s => s.status === 'Active').length },
          { label: 'Expired',           value: subscribers.filter(s => s.status === 'Expired').length },
          { label: 'Cancelled',         value: subscribers.filter(s => s.status === 'Cancelled').length },
        ].map(s => (
          <div className="stat-card" key={s.label}>
            <div className="stat-info">
              <div className="stat-label">{s.label}</div>
              <div className="stat-value">{s.value}</div>
            </div>
          </div>
        ))}
      </div>

      {/* Table Card */}
      <div className="card">
        <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap', marginBottom: 20 }}>
          <div style={{ position: 'relative', flex: 1, minWidth: 200 }}>
            <Search size={14} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} />
            <input
              type="text"
              placeholder="Search subscribers..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="form-control"
              style={{ paddingLeft: 36 }}
            />
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            {['All', 'Active', 'Expired', 'Cancelled'].map(f => (
              <button key={f} onClick={() => setFilter(f)} style={{
                padding: '8px 14px', borderRadius: 8, border: '1px solid',
                borderColor: filter === f ? 'var(--accent-red)' : 'var(--border)',
                background: filter === f ? 'rgba(232,0,29,0.12)' : 'transparent',
                color: filter === f ? 'var(--text-primary)' : 'var(--text-muted)',
                cursor: 'pointer', fontFamily: 'JetBrains Mono, monospace', fontWeight: 600, fontSize: 13,
                transition: 'all 0.2s',
              }}>{f}</button>
            ))}
          </div>
        </div>

        <div className="table-wrapper">
          <table className='table-wide'>
            <thead>
              <tr>
                <th>Sub ID</th><th>Name</th><th>Email</th><th>Plan</th>
                <th>Billing</th><th>Start Date</th><th>Expiry</th>
                <th>Amount</th><th>Status</th><th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(s => (
                <tr key={s.id}>
                  <td style={{ color: 'var(--accent-red)', fontFamily: 'JetBrains Mono, monospace', fontWeight: 700 }}>{s.id}</td>
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <div style={{
                        width: 30, height: 30, borderRadius: '50%',
                        background: 'var(--accent-gradient)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontFamily: 'JetBrains Mono, monospace', fontWeight: 700, fontSize: 12, color: '#fff', flexShrink: 0,
                      }}>{s.name[0]}</div>
                      {s.name}
                    </div>
                  </td>
                  <td style={{ color: 'var(--text-muted)' }}>{s.email}</td>
                  <td><span className="badge badge-info">{s.plan}</span></td>
                  <td style={{ color: 'var(--text-muted)' }}>{s.billing}</td>
                  <td style={{ color: 'var(--text-muted)' }}>{s.start}</td>
                  <td style={{ color: 'var(--text-muted)' }}>{s.expiry}</td>
                  <td style={{ fontFamily: 'JetBrains Mono, monospace', fontWeight: 700 }}>{s.amount}</td>
                  <td><span className={`badge ${statusBadge(s.status)}`}>{s.status}</span></td>
                  <td>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button
                        onClick={() => setViewSub(s)}
                        style={{ background: 'rgba(99,102,241,0.1)', border: 'none', borderRadius: 6, padding: '5px 8px', color: '#818cf8', cursor: 'pointer' }}
                        title="View"
                      ><Eye size={14} /></button>
                      <button
                        onClick={() => setEditSub(s)}
                        style={{ background: 'rgba(255,107,0,0.1)', border: 'none', borderRadius: 6, padding: '5px 8px', color: 'var(--accent-orange)', cursor: 'pointer' }}
                        title="Edit"
                      ><Edit2 size={14} /></button>
                      <button
                        onClick={() => setCancelSub(s)}
                        style={{ background: 'rgba(232,0,29,0.1)', border: 'none', borderRadius: 6, padding: '5px 8px', color: 'var(--accent-red)', cursor: 'pointer' }}
                        title="Cancel"
                        disabled={s.status === 'Cancelled'}
                      ><XCircle size={14} /></button>
                    </div>
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={10} style={{ textAlign: 'center', padding: 32, color: 'var(--text-muted)' }}>No subscribers found.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 20, flexWrap: 'wrap', gap: 12 }}>
          <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>Showing {filtered.length} of {subscribers.length} subscribers</span>
          <div style={{ display: 'flex', gap: 6 }}>
            {[1, 2, 3, 4, 5].map(p => (
              <button key={p} style={{
                width: 32, height: 32, borderRadius: 6, border: '1px solid',
                borderColor: p === 1 ? 'var(--accent-red)' : 'var(--border)',
                background: p === 1 ? 'rgba(232,0,29,0.12)' : 'transparent',
                color: p === 1 ? 'var(--text-primary)' : 'var(--text-muted)',
                cursor: 'pointer', fontFamily: 'JetBrains Mono, monospace', fontWeight: 700, fontSize: 13,
              }}>{p}</button>
            ))}
          </div>
        </div>
      </div>

      {/* Modals */}
      {showAdd   && <AddSubscriptionModal    onClose={() => setShowAdd(false)}    onAdd={handleAdd} />}
      {editSub   && <EditSubscriptionModal   subscription={editSub}  onClose={() => setEditSub(null)}   onSave={handleSave} />}
      {viewSub   && <ViewSubscriptionModal   subscription={viewSub}  onClose={() => setViewSub(null)} />}
      {cancelSub && <CancelSubscriptionModal subscription={cancelSub} onClose={() => setCancelSub(null)} onCancel={handleCancel} />}
    </div>
  );
}

export default Subscription;